import React from 'react'
import { webglLogger } from '@/lib/utils/logger'

interface ErrorBoundaryState {
  hasError: boolean
  error?: Error
}

interface ErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error?: Error; resetError: () => void }>
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    webglLogger.error('ErrorBoundary caught an error', error, { componentStack: errorInfo.componentStack })
  }

  resetError = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultErrorFallback
      return (
        <FallbackComponent
          error={this.state.error}
          resetError={this.resetError}
        />
      )
    }

    return this.props.children
  }
}

function DefaultErrorFallback({
  resetError,
}: {
  error?: Error
  resetError: () => void
}) {
  return (
    <div
      style={{
        minHeight: '100vh',
        backgroundColor: '#000',
        color: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '400px' }}>
        <p style={{ fontSize: '1rem', fontWeight: 300, marginBottom: '1.5rem' }}>
          問題が発生しました
        </p>
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button
            onClick={resetError}
            style={{
              padding: '0.5rem 1.25rem',
              background: 'rgba(240,240,240,0.1)',
              border: '1px solid rgba(240,240,240,0.2)',
              color: '#f0f0f0',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
            }}
          >
            再読み込み
          </button>
          <a
            href="/"
            style={{
              padding: '0.5rem 1.25rem',
              background: 'transparent',
              border: '1px solid rgba(240,240,240,0.2)',
              color: '#f0f0f0',
              textDecoration: 'none',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              display: 'inline-block',
            }}
          >
            ホームへ
          </a>
        </div>
      </div>
    </div>
  )
}
